import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../../../context/auth-context";

/** ---------------------- Tipos da API nova ---------------------- */

export type SaidaItemAPI = {
  id: number | string;
  movimentacao_id: number | string;
  produto_id: number | string;
  estoque_id?: number | string | null;
  quantidade: number | string;
  custo_unitario: string | number | null;
  moeda?: string | null;
  produto: {
    id: number | string;
    codigo: string;
    nome: string;
    posicao: string | null;
    ncm?: string | null;
    classificacao?: string | null;
    peso?: string | number | null;
    dimensao?: string | null;
    ocultar?: boolean | null;
  };
};

export type SaidaMovAPI = {
  movimentacao: {
    id: number | string;
    empresa_id?: number | string | null;
    estoque_id?: number | string | null;
    tipo_movimentacao_id?: number | string | null;
    codigo_movimentacao?: string | null;

    tipo?: "servico" | "venda" | "consumo" | "perda" | "outro" | string | null;
    tipo_full?: string | null;

    responsavel_id: number | string | null;
    responsavel_nome: string | null;

    departamento_origem?: string | null;

    os_numero?: string | null;
    proposta_venda?: string | null;
    empresa_cliente?: string | null;
    cliente_nome?: string | null;
    coletor_nome?: string | null;
    embarcacao?: string | null;

    custo_total?: string | number | null;
    motivo?: string | null;
    origem_mov_id?: number | string | null;
    coordenador_responsavel_ids?: unknown;
    criado_em: string;

    tipo_movimentacao?: {
      codigo?: string | null;
      nome?: string | null;
      natureza?: string | null;
      sinal?: number | string | null;
    };

    estoque?: {
      id?: number | string | null;
      codigo?: string | null;
      nome?: string | null;
      modulo_id?: number | string | null;
    };

    modulo?: {
      id?: number | string | null;
      codigo?: string | null;
      label?: string | null;
    };
  };
  itens: SaidaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  fonte?: "cache" | "banco";
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
  };
  meta: {
    filtros: {
      tipo_saida?: string | null;
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC" | "asc" | "desc";
      limit: number;
      offset: number;
    };
    count: number;
    total?: number;
    has_more?: boolean;
  };
  data: SaidaMovAPI[];
};

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

/** ---------------------- Constantes ---------------------- */

const URL_VENDAS =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-saidas/vendas";

const POLL_FALLBACK_MS = 15000;
const ACCESS_TOKEN_KEY = "access_token";

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

const isNA = (s?: string | null) =>
  !s || s.trim().length === 0 || s.trim().toLowerCase() === "n/a";

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
      return {
        data: iso,
        hora: "",
      };
    }

    const data = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return { data, hora };
  } catch {
    return { data: iso, hora: "" };
  }
}

function parseNumber(value: unknown): number {
  const n =
    typeof value === "number"
      ? value
      : Number(String(value ?? "0").replace(",", "."));

  return Number.isFinite(n) ? n : 0;
}

function formatBRL(x: number): string {
  return x.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatMoney(valor: number, moeda?: string | null): string {
  const currency = String(moeda || "BRL").toUpperCase();

  if (currency === "BRL") return `R$ ${formatBRL(valor)}`;

  const symbol = currency === "USD" ? "$" : currency === "EUR" ? "€" : currency;

  return `${symbol} ${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function isVenda(mov: SaidaMovAPI["movimentacao"]): boolean {
  const tipo = String(mov.tipo ?? "").toLowerCase();
  const tipoFull = String(mov.tipo_full ?? "").toLowerCase();
  const codigoMov = String(mov.codigo_movimentacao ?? "").toUpperCase();
  const tipoMovCodigo = String(
    mov.tipo_movimentacao?.codigo ?? ""
  ).toUpperCase();

  if (codigoMov === "SAI-VENDA") return true;
  if (tipoMovCodigo === "SAI-VENDA") return true;
  if (tipo === "venda") return true;
  if (tipoFull === "standby-venda-saida") return true;
  if (tipoFull === "venda-baixa") return true;
  if (tipoFull === "saida-venda") return true;
  if (tipoFull.includes("venda")) return true;

  return false;
}

function getPvOuOs(mov: SaidaMovAPI["movimentacao"]): string {
  const proposta = mov.proposta_venda?.trim() ?? "";
  const os = mov.os_numero?.trim() ?? "";

  if (!isNA(proposta)) return proposta;
  if (!isNA(os)) return os;

  return "-";
}

function findModuloAtualFromSessao(
  sessao: any,
  areaSlug?: string,
  estoqueSlug?: string
): ModuloAtual | null {
  const area = normalizeComparable(areaSlug || "suprimentos");
  const estoque = normalizeComparable(
    estoqueSlug || "estoque-p-servico-e-comercial"
  );

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoque] || estoque;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (area && depSlug && depSlug !== area) {
        continue;
      }

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoque ||
            modLabel === estoque ||
            modCodigo.includes(estoque) ||
            estoque.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

/** ---------------------- Tabela ---------------------- */

/**
 * Ordem precisa bater com os headers do componente:
 * ["OS","Tipo","Código","Nome do Produto","Quantidade","Custo Unitário","Técnico","Data","Hora"]
 *
 * Mantive a mesma estrutura antiga para não quebrar as telas que usam esse hook.
 * A coluna "Técnico" continua recebendo o cliente, porque o arquivo antigo já fazia isso.
 */
export type SaidaRowUI = [
  string, // OS / PV
  string, // Tipo
  string, // Código
  string, // Nome do Produto
  string, // Quantidade
  string, // Custo Unitário
  string, // Cliente
  string, // Data
  string // Hora
];

function flattenToRows(movs: SaidaMovAPI[]): SaidaRowUI[] {
  const rows: SaidaRowUI[] = [];

  for (const m of movs || []) {
    if (!isVenda(m.movimentacao)) continue;

    const { data, hora } = toBRT(m.movimentacao.criado_em);

    const pv = getPvOuOs(m.movimentacao);
    const tipo = m.movimentacao.tipo ?? "venda";

    const clienteNome = m.movimentacao.cliente_nome?.trim() ?? "";
    const empresaCliente = m.movimentacao.empresa_cliente?.trim() ?? "";

    const cliente = !isNA(clienteNome)
      ? clienteNome
      : !isNA(empresaCliente)
      ? empresaCliente
      : "-";

    for (const it of m.itens || []) {
      const qtd = parseNumber(it.quantidade);
      const custo = parseNumber(it.custo_unitario);
      const moeda = String(it.moeda || "BRL").toUpperCase();

      rows.push([
        pv,
        String(tipo),
        String(it.produto?.codigo ?? "").toUpperCase(),
        String(it.produto?.nome ?? "").toUpperCase(),
        String(qtd),
        formatMoney(custo, moeda),
        cliente,
        data,
        hora,
      ]);
    }
  }

  return rows;
}

/** ---------------------- Fetchers ---------------------- */

export type VendasQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

export async function fetchVendasOnce(
  moduloAtual: ModuloAtual | null,
  query?: VendasQuery
): Promise<{
  rows: SaidaRowUI[];
  meta: RespAPI["meta"] | null;
  resp: RespAPI | null;
}> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota");
  }

  const payload = {
    modulo_id: moduloAtual.id,
    order: query?.order ?? "desc",
    limit: query?.limit ?? 10000,
    offset: query?.offset ?? 0,
    ...(query?.date_from ? { date_from: query.date_from } : {}),
    ...(query?.date_to ? { date_to: query.date_to } : {}),
  };

  const res = await fetch(URL_VENDAS, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const json = (await res.json().catch(() => null)) as RespAPI | null;

  if (!res.ok || json?.ok === false) {
    const erro =
      (json as any)?.erro ||
      (Array.isArray((json as any)?.erros) &&
        (json as any).erros.join(" | ")) ||
      `HTTP ${res.status}`;

    throw new Error(erro);
  }

  const dataRaw: SaidaMovAPI[] =
    json && Array.isArray(json.data) ? json.data : [];

  return {
    rows: flattenToRows(dataRaw),
    meta: json?.meta ?? null,
    resp: json,
  };
}

/** ---------------------- Hook ---------------------- */

export function useVendasSuprimentos(query?: VendasQuery) {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug?: string;
    estoqueSlug?: string;
  }>();

  const { sessao, loading: authLoading } = useAuth();

  const moduloAtual = useMemo(
    () =>
      findModuloAtualFromSessao(
        sessao,
        areaSlug || "suprimentos",
        estoqueSlug || "estoque-p-servico-e-comercial"
      ),
    [sessao, areaSlug, estoqueSlug]
  );

  const [rows, setRows] = useState<SaidaRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [resp, setResp] = useState<RespAPI | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const r = await fetchVendasOnce(moduloAtual, query);

      setRows(r.rows);
      setMeta(r.meta);
      setResp(r.resp);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar vendas.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    setLoading(true);
    setErro(null);
    setRows([]);
    setMeta(null);
    setResp(null);

    if (authLoading) {
      return () => {
        stop.current = true;
      };
    }

    if (!moduloAtual?.id) {
      setLoading(false);
      setErro("Módulo de estoque não identificado para esta rota.");
      return () => {
        stop.current = true;
      };
    }

    (async () => {
      try {
        await carregar();
        setLoading(false);

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          await sleep(jitter(POLL_FALLBACK_MS));

          if (stop.current) break;

          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useVendasSuprimentos loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloAtual?.id, authLoading, JSON.stringify(query)]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    loading: authLoading || loading,
    refresh,
    meta,
    resp,
    erro,
    moduloAtual,
  };
}
